// The editor's picture: the game's own engraving renderer looking straight down at the map (mapView's
// buildMap, rebuilt after edits), with a plain overlay pass on top for what only the author sees — the
// half tint, the diagonal, bounds, markers, selection handles, validation rings and the debug layers.
import * as THREE from 'three';
import { createEngraver } from '../engrave.js';
import { buildMap, type MapView } from '../mapView.js';
import { PROP_SHAPES, layoutFromMap, terrain, type Layout } from '../../core/layout.js';
import { expandMap, mirrorPt } from '../../core/map/expand.js';
import type { MapData, Patch, Prop, Pt } from '../../core/map/schema.js';
import { anchor, getItem, isClosed, vertices, type ObjKey, type Ref } from './state.js';

export type Decorations = {
  selected: Ref | null;
  selectedVertex: number;
  hover: Ref | null;
  issues: Ref[];
  draft: { key: ObjKey; pts: Pt[]; cursor: Pt | null; closed: boolean } | null;
  showIssues: boolean;
};

// selection colours must read against the cream paper and the dimmed blue half alike
const COLORS = { red: 0xc9533f, blue: 0x3f64a0, ink: 0x2c1e12, gold: 0xb8862b, sel: 0xd4009f, hover: 0x008c8c, issue: 0xff1a1a, draft: 0xd4009f, size: 0x0096c8 };
/* A long lens from high up: close to a plan view, so a tree's crown stays over its trunk even at the map's edge. */
const FOV = 12;

/* Sight blockers as layout.ts rasterises them (TREE_BLOCK there): a tree crown blocks 1.05 m around its trunk;
   buildings and sight-blocking props (PROP_SHAPES) block their own footprint. */
const TREE_SIGHT_R = 1.05;

export function createEditorView(canvas: HTMLCanvasElement, stageEl: HTMLElement) {
  const eng = createEngraver(canvas, stageEl);
  const { scene, camera, renderer } = eng;
  camera.fov = FOV;
  camera.far = 3000;
  camera.up.set(0, 0, -1);  // screen up is -z: team a's base (x < 0, z > 0) sits bottom-left
  camera.updateProjectionMatrix();
  eng.uniforms.uFar.value = camera.far;
  // the game's shadow box covers the area around the hero; the editor looks at the whole map
  const sun = scene.children.find((o): o is THREE.DirectionalLight => (o as THREE.DirectionalLight).isDirectionalLight);
  if (sun) {
    Object.assign(sun.shadow.camera, { left: -100, right: 100, top: 100, bottom: -100, far: 140 });
    sun.shadow.camera.updateProjectionMatrix();
  }

  const overlay = new THREE.Scene();
  const staticLayer = new THREE.Group(), liveLayer = new THREE.Group(), debugLayer = new THREE.Group();
  overlay.add(debugLayer, staticLayer, liveLayer);

  const cam = { x: 0, z: 0, h: 450 };
  let viewH = 1;
  function applyCamera() {
    camera.position.set(cam.x, cam.h, cam.z);
    camera.lookAt(cam.x, 0, cam.z);
    camera.updateMatrixWorld();
    eng.followSun(cam.x, cam.z);
  }

  /* ---- rendering on demand; the fallback timer keeps a hidden tab (rAF paused) drawing too ---- */
  let frameQueued = false;
  function draw() {
    if (!frameQueued) return;
    frameQueued = false;
    applyCamera();
    eng.render();
    renderer.autoClear = false;
    renderer.setRenderTarget(null);
    renderer.render(overlay, camera);
    renderer.autoClear = true;
  }
  function requestDraw() {
    if (frameQueued) return;
    frameQueued = true;
    requestAnimationFrame(draw);
    setTimeout(draw, 200);
  }
  new ResizeObserver(() => {
    viewH = Math.max(1, stageEl.getBoundingClientRect().height);
    requestDraw();
  }).observe(stageEl);
  viewH = Math.max(1, stageEl.getBoundingClientRect().height);

  /** Metres per screen pixel on the ground. */
  const metresPerPixel = () => 2 * cam.h * Math.tan(THREE.MathUtils.degToRad(FOV / 2)) / viewH;

  function screenToWorld(clientX: number, clientY: number): Pt {
    const r = canvas.getBoundingClientRect(), mpp = metresPerPixel();
    return [cam.x + (clientX - r.left - r.width / 2) * mpp, cam.z + (clientY - r.top - r.height / 2) * mpp];
  }

  function pan(dxPixels: number, dyPixels: number) {
    const mpp = metresPerPixel();
    cam.x -= dxPixels * mpp; cam.z -= dyPixels * mpp;
    requestDraw();
  }
  /** Zooms by factor k keeping the ground point under the cursor fixed. */
  function zoomAt(clientX: number, clientY: number, k: number) {
    const before = screenToWorld(clientX, clientY);
    cam.h = Math.min(1400, Math.max(40, cam.h * k));
    const after = screenToWorld(clientX, clientY);
    cam.x += before[0] - after[0]; cam.z += before[1] - after[1];
    requestDraw();
  }
  function frame(map: MapData) {
    const r = stageEl.getBoundingClientRect(), aspect = r.width / Math.max(1, r.height);
    const halfTan = Math.tan(THREE.MathUtils.degToRad(FOV / 2));
    cam.x = 0; cam.z = 0;
    cam.h = Math.max(map.height / 2 / halfTan, map.width / 2 / halfTan / aspect) * 1.08;
    requestDraw();
  }

  /* ---- the engraved map, rebuilt after edits (throttled: a drag rebuilds a few times a second) ---- */
  let mapView: MapView | null = null, layout: Layout | null = null;
  let pendingMap: MapData | null = null, rebuildTimer: ReturnType<typeof setTimeout> | null = null;
  let lastRebuildMs = 0, onRebuilt: (ms: number, err: unknown) => void = () => {};
  function rebuildNow() {
    rebuildTimer = null;
    const map = pendingMap;
    if (!map) return;
    pendingMap = null;
    const t0 = performance.now();
    let err: unknown = null;
    try {
      mapView?.dispose();
      mapView = null;
      layout = layoutFromMap(map);
      mapView = buildMap(scene, layout);
    } catch (e) {
      err = e;
    }
    lastRebuildMs = performance.now() - t0;
    onRebuilt(lastRebuildMs, err);
    if (overlays.nav) refreshNav();
    requestDraw();
  }
  function setMap(map: MapData, immediate = false) {
    pendingMap = map;
    if (immediate) { if (rebuildTimer) clearTimeout(rebuildTimer); rebuildNow(); return; }
    if (!rebuildTimer) rebuildTimer = setTimeout(rebuildNow, Math.min(400, 60 + lastRebuildMs));
  }

  /* ---- overlay primitives ---- */
  const mats = new Map<string, THREE.Material>();
  const lineMat = (color: number, opacity = 1) => {
    const k = `l${color}|${opacity}`;
    if (!mats.has(k)) mats.set(k, new THREE.LineBasicMaterial({ color, transparent: opacity < 1, opacity, depthTest: false }));
    return mats.get(k) as THREE.LineBasicMaterial;
  };
  const fillMat = (color: number, opacity: number) => {
    const k = `f${color}|${opacity}`;
    if (!mats.has(k)) mats.set(k, new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthTest: false, depthWrite: false, side: THREE.DoubleSide }));
    return mats.get(k) as THREE.MeshBasicMaterial;
  };
  // overlay draws without depth testing, so it needs no height; at ground level it lines up with the
  // positions it marks (anything raised would drift outward in the perspective view)
  const Y = 0.2;
  const v3 = ([x, z]: Pt, y = Y) => new THREE.Vector3(x, y, z);
  /* WebGL draws 1 px lines; a bold one is the same line again, nudged a pixel each way. */
  function line(group: THREE.Group, pts: Pt[], color: number, { closed = false, opacity = 1, y = Y, bold = false } = {}) {
    const o = metresPerPixel() * 0.8;
    for (const [dx, dz] of bold ? [[0, 0], [o, 0], [-o, 0], [0, o], [0, -o]] : [[0, 0]]) {
      const g = new THREE.BufferGeometry().setFromPoints(pts.map(([x, z]) => v3([x + dx!, z + dz!], y)));
      const l = closed ? new THREE.LineLoop(g, lineMat(color, opacity)) : new THREE.Line(g, lineMat(color, opacity));
      l.renderOrder = 2;
      group.add(l);
    }
  }
  const circlePts = ([x, z]: Pt, r: number, n = 32): Pt[] => Array.from({ length: n }, (_, i) => [x + Math.cos(i / n * 2 * Math.PI) * r, z + Math.sin(i / n * 2 * Math.PI) * r]);
  function fill(group: THREE.Group, pts: Pt[], color: number, opacity: number, y = Y) {
    if (pts.length < 3) return;
    const shape = new THREE.Shape(pts.map(([x, z]) => new THREE.Vector2(x, -z)));
    const m = new THREE.Mesh(new THREE.ShapeGeometry(shape), fillMat(color, opacity));
    m.rotation.x = -Math.PI / 2;
    m.position.y = y;
    m.renderOrder = 1;
    group.add(m);
  }
  function square(group: THREE.Group, [x, z]: Pt, sizePx: number, color: number) {
    const s = sizePx * metresPerPixel() / 2;
    fill(group, [[x - s, z - s], [x + s, z - s], [x + s, z + s], [x - s, z + s]], color, 1, Y);
    line(group, [[x - s, z - s], [x + s, z - s], [x + s, z + s], [x - s, z + s]], COLORS.ink, { closed: true });
  }
  function clear(group: THREE.Group) {
    for (const o of [...group.children]) {
      group.remove(o);
      (o as THREE.Mesh).geometry?.dispose();
      const m = (o as THREE.Mesh).material as THREE.Material | undefined;
      if (m && ![...mats.values()].includes(m)) { (m as THREE.MeshBasicMaterial).map?.dispose(); m.dispose(); }
    }
  }

  /** Corners of an oriented rectangle (building convention: rot turns local x toward +z). */
  const rectPts = (o: { x: number; z: number; rot: number }, hw: number, hd: number): Pt[] => {
    const c = Math.cos(o.rot), s = Math.sin(o.rot);
    return ([[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]] as Pt[]).map(([lx, lz]) => [o.x + lx * c - lz * s, o.z + lx * s + lz * c]);
  };
  const propPts = (p: Prop): Pt[] => {
    const { r, w, d } = PROP_SHAPES[p.kind];
    return r !== undefined ? circlePts([p.x, p.z], r, 20) : rectPts(p, w! / 2, d! / 2);
  };
  const ellipsePts = (b: { x: number; z: number; rx: number; rz: number; rot: number }): Pt[] => {
    const c = Math.cos(b.rot), s = Math.sin(b.rot);
    return Array.from({ length: 40 }, (_, i) => {
      const a = i / 40 * 2 * Math.PI, lx = Math.cos(a) * b.rx, lz = Math.sin(a) * b.rz;
      return [b.x + lx * c - lz * s, b.z + lx * s + lz * c] as Pt;
    });
  };

  /** The outline an item is drawn with when selected, hovered or flagged. */
  function outline(key: ObjKey, item: ReturnType<typeof getItem>): { pts: Pt[]; closed: boolean } {
    const pts = vertices(key, item);
    if (pts) return { pts, closed: isClosed(key, item) };
    if (key === 'buildings' || key === 'fields' || key === 'fords') { const b = item as Patch; return { pts: rectPts(b, b.w / 2, b.d / 2), closed: true }; }
    if (key === 'props') return { pts: propPts(item as Prop), closed: true };
    if (key === 'brush') return { pts: ellipsePts(item as { x: number; z: number; rx: number; rz: number; rot: number }), closed: true };
    const r = key === 'bridges' ? 2.5 : key === 'camps' ? 1.5 : key === 'decor' ? 0.8 * (item as { scale: number }).scale : 1.1;
    return { pts: circlePts(anchor(key, item), r, 24), closed: true };
  }
  function refOutline(map: MapData, ref: Ref): { pts: Pt[]; closed: boolean } {
    if (ref.kind === 'obj') return outline(ref.key, getItem(map, ref));
    const p = ref.kind === 'base' ? map.base : ref.kind === 'side' ? map.points.side : map.den.pos;
    return { pts: circlePts(p, ref.kind === 'side' ? map.pointRadius : ref.kind === 'base' ? 4.2 : 4), closed: true };
  }

  /* ---- static overlay: halves, diagonal, bounds, point markers ---- */
  /** The map rectangle clipped to one side of the base-to-base line (sign +1: team a's side). */
  function halfPolygon(map: MapData, sign: 1 | -1): Pt[] {
    const hw = map.width / 2, hh = map.height / 2, [bx, bz] = map.base;
    const side = ([x, z]: Pt) => sign * (bz * x - bx * z);  // >= 0 on the wanted side
    const rect: Pt[] = [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]], out: Pt[] = [];
    for (let i = 0; i < 4; i++) {
      const a = rect[i]!, b = rect[(i + 1) % 4]!, sa = side(a), sb = side(b);
      if (sa >= 0) out.push(a);
      if ((sa >= 0) !== (sb >= 0)) { const t = sa / (sa - sb); out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]); }
    }
    return out;
  }

  let currentMap: MapData | null = null;
  function drawStatic(map: MapData) {
    currentMap = map;
    clear(staticLayer);
    const hw = map.width / 2, hh = map.height / 2;
    fill(staticLayer, halfPolygon(map, 1), COLORS.red, 0.07, 0.1);
    fill(staticLayer, halfPolygon(map, -1), 0x1a2a48, 0.28, 0.1);
    line(staticLayer, [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]], COLORS.ink, { closed: true });
    const [bx, bz] = map.base, len = Math.hypot(bx, bz) || 1, reach = Math.hypot(hw, hh);
    line(staticLayer, [[-bx / len * reach, -bz / len * reach], [bx / len * reach, bz / len * reach]], COLORS.gold, { bold: true });
    const e = expandMap(map);
    line(staticLayer, circlePts(e.bases.a, 4.6), COLORS.red); line(staticLayer, circlePts(e.bases.b, 4.6), COLORS.blue, { opacity: 0.6 });
    line(staticLayer, circlePts(e.points[0]!.pos, map.pointRadius + 0.3), COLORS.red);
    line(staticLayer, circlePts(e.points[1]!.pos, map.pointRadius + 0.3), COLORS.gold);
    line(staticLayer, circlePts(e.points[2]!.pos, map.pointRadius + 0.3), COLORS.blue, { opacity: 0.6 });
    line(staticLayer, circlePts(e.dens[0]!.pos, 4.4), COLORS.red); line(staticLayer, circlePts(e.dens[1]!.pos, 4.4), COLORS.blue, { opacity: 0.6 });
    refreshDebug();
    requestDraw();
  }

  /* ---- debug layers: walkability grid and sight blockers ---- */
  const overlays = { nav: false, sight: false };
  let navTimer: ReturnType<typeof setTimeout> | null = null;
  function refreshNav() {
    if (navTimer) clearTimeout(navTimer);
    navTimer = setTimeout(() => { navTimer = null; drawDebug(); requestDraw(); }, 250);
  }
  function drawDebug() {
    clear(debugLayer);
    if (!currentMap) return;
    const L = layout ?? layoutFromMap(currentMap);
    if (overlays.nav) {
      const nav = terrain(L).buildNav();
      const data = new Uint8Array(nav.w * nav.h * 4);
      for (let iz = 0; iz < nav.h; iz++) for (let ix = 0; ix < nav.w; ix++) {
        const k = ((nav.h - 1 - iz) * nav.w + ix) * 4;  // texture rows run +z to -z once the plane lies flat
        if (!nav.walk[iz * nav.w + ix]) { data[k] = 200; data[k + 1] = 30; data[k + 2] = 30; data[k + 3] = 130; }
      }
      const tex = new THREE.DataTexture(data, nav.w, nav.h, THREE.RGBAFormat);
      tex.magFilter = THREE.NearestFilter; tex.needsUpdate = true;
      const plane = new THREE.Mesh(new THREE.PlaneGeometry(nav.w * nav.cell, nav.h * nav.cell),
        new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false }));
      plane.rotation.x = -Math.PI / 2;
      plane.position.set(nav.minX + nav.w * nav.cell / 2, 0.12, nav.minZ + nav.h * nav.cell / 2);
      debugLayer.add(plane);
    }
    if (overlays.sight) {
      for (const t of L.trees) fill(debugLayer, circlePts(t, TREE_SIGHT_R, 14), 0x1b1bff, 0.45, 0.15);
      for (const b of L.buildings) fill(debugLayer, rectPts(b, b.w / 2, b.d / 2), 0x1b1bff, 0.45, 0.15);
      for (const p of L.props) if (PROP_SHAPES[p.kind].sight) fill(debugLayer, propPts(p), 0x1b1bff, 0.45, 0.15);
    }
  }
  function refreshDebug() {
    if (overlays.nav || overlays.sight) refreshNav();
    else clear(debugLayer);
  }
  function setOverlay(name: 'nav' | 'sight', on: boolean) {
    overlays[name] = on;
    if (on) { drawDebug(); } else refreshDebug();
    requestDraw();
  }

  /* ---- live overlay: selection, hover, handles, draft, validation rings ---- */
  function decorate(map: MapData, d: Decorations) {
    clear(liveLayer);
    if (d.showIssues) {
      for (const ref of d.issues) {
        try { const o = refOutline(map, ref); line(liveLayer, o.pts, COLORS.issue, { closed: o.closed, bold: true }); } catch { /* an issue on an index that no longer exists */ }
      }
    }
    if (d.hover) { const o = refOutline(map, d.hover); line(liveLayer, o.pts, COLORS.hover, { closed: o.closed, bold: true }); }
    if (d.selected) {
      const o = refOutline(map, d.selected);
      line(liveLayer, o.pts, COLORS.sel, { closed: o.closed, bold: true });
      if (d.selected.kind === 'obj') {
        const { key } = d.selected, item = getItem(map, d.selected);
        // the live mirror of what is selected, so the author sees both copies move
        const mirrored = o.pts.map(mirrorPt);
        if (d.selected.part === 'half') line(liveLayer, mirrored, COLORS.sel, { closed: o.closed, opacity: 0.6 });
        const pts = vertices(key, item);
        if (pts) pts.forEach((p, i) => square(liveLayer, p, i === d.selectedVertex ? 12 : 9, i === d.selectedVertex ? COLORS.red : COLORS.sel));
        for (const h of sizeHandles(key, item)) square(liveLayer, h.pos, 10, COLORS.size);
      }
    }
    if (d.draft && d.draft.pts.length) {
      const pts = d.draft.cursor ? [...d.draft.pts, d.draft.cursor] : d.draft.pts;
      line(liveLayer, pts, COLORS.draft, { closed: d.draft.closed && pts.length > 2, bold: true });
      d.draft.pts.forEach(p => square(liveLayer, p, 8, COLORS.draft));
    }
    requestDraw();
  }

  return {
    eng, canvas, requestDraw, screenToWorld, metresPerPixel, pan, zoomAt, frame, setMap, drawStatic, decorate, setOverlay,
    set onRebuilt(f: (ms: number, err: unknown) => void) { onRebuilt = f; },
  };
}

export type SizeHandle = { field: 'w' | 'd' | 'rx' | 'rz'; pos: Pt };

/** Drag handles for an item's size: a rectangle's (building, field, ford) w/d and a brush's rx/rz, on its
    local +x and +z sides. */
export function sizeHandles(key: ObjKey, item: unknown): SizeHandle[] {
  const rect = key === 'buildings' || key === 'fields' || key === 'fords';
  if (!rect && key !== 'brush') return [];
  const o = item as { x: number; z: number; rot: number; w: number; d: number; rx: number; rz: number };
  const c = Math.cos(o.rot), s = Math.sin(o.rot);
  const [a, b] = rect ? [o.w / 2, o.d / 2] : [o.rx, o.rz];
  return [
    { field: rect ? 'w' : 'rx', pos: [o.x + a * c, o.z + a * s] },
    { field: rect ? 'd' : 'rz', pos: [o.x - b * s, o.z + b * c] },
  ];
}

export type EditorView = ReturnType<typeof createEditorView>;
